import { zodResolver } from "@hookform/resolvers/zod";
import { getGtmIdFromCT, getPdp, gtmAddPdpProductToCart, gtmAddToWishlist, gtmAdpageAddToCart, gtmRemoveFromWishlist, } from "@jog/shared";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { usePending } from "../components";
import { useAddedProduct } from "./useAddedProduct";
import { getPdpPrice } from "./useProductDetail";
const cartSchema = z.object({
    products: z.array(z.object({ sku: z.string(), id: z.string() })),
});
const CoordSetAndLookContext = createContext({});
export const CoordSetAndLookProvider = ({ children, products, addProductsToCart, }) => {
    const [productData, setProductData] = useState(products.reduce((acc, cur) => ({ ...acc, [cur.id]: { prismic: cur } }), {}));
    const form = useForm({
        resolver: zodResolver(cartSchema),
        defaultValues: { products: [] },
    });
    const selectedProducts = form.watch("products").filter((i) => i.sku && i.id);
    const total = useMemo(() => selectedProducts.reduce((pre, cur) => {
        var _a;
        const product = (_a = productData === null || productData === void 0 ? void 0 : productData[cur.id]) === null || _a === void 0 ? void 0 : _a.ct;
        if (!product)
            return pre;
        return pre + getPdpPrice(product).currentPrice;
    }, 0), [selectedProducts, productData]);
    const { setPending } = usePending();
    const { addedProduct } = useAddedProduct();
    const onSubmit = useCallback(async (values, event) => {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        const skus = values.products.filter((i) => i.sku && i.id);
        if (skus.length === 0) {
            const $form = event === null || event === void 0 ? void 0 : event.target;
            if ($form) {
                $form.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }
        setPending(true);
        await addProductsToCart(skus.map((i) => ({ sku: i.sku, quantity: 1 }))).finally(() => setPending(false));
        addedProduct(skus.map((i) => i.sku));
        values.products.forEach((i) => {
            var _a;
            const product = (_a = productData === null || productData === void 0 ? void 0 : productData[i.id]) === null || _a === void 0 ? void 0 : _a.ct;
            if (product) {
                gtmAddPdpProductToCart(product, window.location.href);
                gtmAdpageAddToCart(product, i.sku, getGtmIdFromCT(product.attributes), window.location.href);
            }
        });
    }, [addProductsToCart, addedProduct, productData, setPending]);
    return (<CoordSetAndLookContext.Provider value={{ productData, form, setProductData, selectedProducts, total, onSubmit }}>
            {children}
        </CoordSetAndLookContext.Provider>);
};
export const useCoordSetAndLook = () => {
    return useContext(CoordSetAndLookContext);
};
export const useCoordSetAndLookProduct = ({ prismic, ct }) => {
    const skus = useMemo(() => {
        switch (ct === null || ct === void 0 ? void 0 : ct.sizeGroup.type) {
            case "size":
                return ct === null || ct === void 0 ? void 0 : ct.sizeGroup.sizes.map((i) => ({
                    sku: i.sku,
                    size: i.size,
                    disabled: !i.isOnStock || i.availableQuantity === 0,
                }));
            case "waist_length":
                return ct === null || ct === void 0 ? void 0 : ct.sizeGroup.sizes.map((i) => ({
                    sku: i.sku,
                    size: `${i.waist}/${i.length}`,
                    disabled: !i.isOnStock || i.availableQuantity === 0,
                }));
            default:
                return [];
        }
    }, [ct]);
    const [pending, setPending] = useState(true);
    const { setProductData } = useCoordSetAndLook();
    useEffect(() => {
        getPdp(prismic.id)
            .then((ct) => setProductData((prev) => ({ ...prev, [prismic.id]: { prismic, ct } })))
            .catch(() => { })
            .finally(() => setPending(false));
    }, [prismic, setProductData]);
    return { skus, pending };
};
export const useCoordSetAndLookWishlist = ({ addWishlistItem, removeWishlistItem, simpleWishlist, }, { prismic, ct }) => {
    const lineItemId = useMemo(() => { var _a, _b; return (_b = (_a = simpleWishlist === null || simpleWishlist === void 0 ? void 0 : simpleWishlist.lineItems) === null || _a === void 0 ? void 0 : _a.find((lineItem) => lineItem.productId === (ct === null || ct === void 0 ? void 0 : ct.id))) === null || _b === void 0 ? void 0 : _b.id; }, [simpleWishlist, ct]);
    const onFavorite = useCallback(async (e) => {
        var _a;
        e.preventDefault();
        if (!ct)
            return;
        const price = ((_a = (ct.firstPrice || ct.price)) === null || _a === void 0 ? void 0 : _a.centAmount) || 0;
        const gtmPrice = +(price / 100).toFixed(2);
        const gtmItem = {
            id: getGtmIdFromCT((ct === null || ct === void 0 ? void 0 : ct.attributes) || []),
            name: prismic.name,
            price: gtmPrice,
        };
        if (!lineItemId) {
            gtmAddToWishlist([gtmItem]);
            await addWishlistItem(ct.id);
        }
        else {
            gtmRemoveFromWishlist([gtmItem]);
            await removeWishlistItem(lineItemId);
        }
    }, [ct, prismic, lineItemId, addWishlistItem, removeWishlistItem]);
    return { onFavorite, lineItemId };
};
